import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestBlob, requestJson } from '../../lib/util';
import {
  CardApprovalDecision,
  CardApprovalDecisionResponse,
  CardApprovalsResponse,
  CardOverviewResponse
} from './types';

export * from './types';

/**
 * The legacy backend forwards these to the new backend, which owns the card
 * logic and talks to Swan.
 */
const approvalsPath = (companyId: string) => `/api/v2/companies/${companyId}/pxtra-card/approvals`;

export const useListCardApprovals = (companyId: string, options = {}) =>
  useQuery<CardApprovalsResponse>({
    queryKey: ['cardApprovals', companyId],
    queryFn: () => requestJson<CardApprovalsResponse>({ path: approvalsPath(companyId) }),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
    ...options
  });

export const useListCards = (companyId: string, options = {}) =>
  useQuery<CardOverviewResponse>({
    queryKey: ['cardOverview', companyId],
    queryFn: () =>
      requestJson<CardOverviewResponse>({
        path: `/api/v2/companies/${companyId}/pxtra-card/cards`
      }),
    enabled: !!companyId,
    ...options
  });

/** The same list and the same field set, as a file. */
export const useExportCards = (companyId: string) =>
  useMutation({
    mutationFn: () =>
      requestBlob({
        path: `/api/v2/companies/${companyId}/pxtra-card/cards?format=csv`
      })
  });

export const useDecideCardApprovals = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestIds,
      decision
    }: {
      requestIds: string[];
      decision: CardApprovalDecision;
    }) =>
      requestJson<CardApprovalDecisionResponse>({
        path: `${approvalsPath(companyId)}/decision`,
        method: 'POST',
        body: { request_ids: requestIds, decision }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cardApprovals', companyId] });
      // A decision moves the person along on the status list too.
      queryClient.invalidateQueries({ queryKey: ['cardOverview', companyId] });
    }
  });
};
export * from './onboarding';
export * from './offer';
export * from './wallet';
export * from './walletError';
export * from './walletKeys';
